import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Wand2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';
import { createCustomer, updateCustomer, type Customer } from '@/lib/reseller-api';
import { fetchUsers, createUser, createGroup, linkGroup } from '@/lib/traccar-api';
import { cn } from '@/lib/utils';

export type TraccarMode = 'none' | 'existing' | 'new';

interface CustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCustomer?: Customer | null;
  defaultTraccarMode?: TraccarMode;
  onSaved?: (customer: Customer) => void;
}

const PASSWORD_CHARS = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$';
const generatePassword = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((b) => PASSWORD_CHARS[b % PASSWORD_CHARS.length])
    .join('');

export function CustomerDialog({
  open,
  onOpenChange,
  editingCustomer,
  defaultTraccarMode,
  onSaved,
}: CustomerDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const [traccarMode, setTraccarMode] = useState<TraccarMode>('none');
  const [selectedTraccarUserId, setSelectedTraccarUserId] = useState<string>('');
  const [traccarUserOpen, setTraccarUserOpen] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: traccarUsers = [] } = useQuery({
    queryKey: ['traccar-users'],
    queryFn: fetchUsers,
    enabled: open && traccarMode === 'existing',
  });

  useEffect(() => {
    if (!open) return;
    const mode: TraccarMode =
      defaultTraccarMode && (editingCustomer || defaultTraccarMode !== 'none')
        ? defaultTraccarMode
        : editingCustomer
          ? editingCustomer.traccar_user_id ? 'existing' : 'none'
          : 'existing';
    setTraccarMode(mode);
    setSelectedTraccarUserId(
      editingCustomer?.traccar_user_id ? String(editingCustomer.traccar_user_id) : '',
    );
    setNewUserPassword('');
    setTraccarUserOpen(false);
  }, [open, editingCustomer, defaultTraccarMode]);

  const selectedTraccarUser = traccarUsers.find((user) => String(user.id) === selectedTraccarUserId);
  const selectedTraccarUserLabel = selectedTraccarUser
    ? `${selectedTraccarUser.name} (${selectedTraccarUser.email})`
    : '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: Partial<Customer> = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      tax_id: formData.get('tax_id') as string,
      customer_type: formData.get('customer_type') as Customer['customer_type'],
      status: formData.get('status') as Customer['status'],
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      town: formData.get('town') as string,
      state: formData.get('state') as string,
      postal_code: formData.get('postal_code') as string,
      notes: formData.get('notes') as string,
    };

    if (traccarMode === 'existing' && !selectedTraccarUserId) {
      toast({
        title: t('common.error'),
        description: t('reseller.customer.selectUser'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (traccarMode === 'new') {
        const user = await createUser({
          name: data.name as string,
          email: data.email as string,
          password: newUserPassword,
        });
        data.traccar_user_id = user.id;
        const group = await createGroup({ name: data.name as string });
        await linkGroup(user.id, group.id);
        queryClient.invalidateQueries({ queryKey: ['traccar-users'] });
        queryClient.invalidateQueries({ queryKey: ['groups'] });
      } else if (traccarMode === 'existing' && selectedTraccarUserId) {
        data.traccar_user_id = Number(selectedTraccarUserId);
      } else {
        data.traccar_user_id = null;
      }

      const saved = editingCustomer
        ? await updateCustomer(editingCustomer.id, data)
        : await createCustomer(data);

      queryClient.setQueryData<Customer[]>(['customers'], (old) => {
        if (!old) return [saved];
        if (editingCustomer) return old.map((c) => (c.id === saved.id ? saved : c));
        return [...old, saved];
      });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (traccarMode === 'new') {
        await queryClient.refetchQueries({ queryKey: ['permissions-map'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
      }

      toast({
        title: editingCustomer
          ? t('reseller.customer.updated')
          : t('reseller.customer.created'),
      });
      onSaved?.(saved);
      onOpenChange(false);
    } catch (error) {
      const msg = (error as Error).message || '';
      toast({
        title: t('common.error'),
        description: msg.length > 120 ? msg.slice(0, 120) + '…' : msg,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCustomer ? t('reseller.customer.edit') : t('reseller.customer.add')}
          </DialogTitle>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('reseller.customer.name')} *</Label>
              <Input id="name" name="name" required defaultValue={editingCustomer?.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('reseller.customer.phone')}</Label>
              <Input id="phone" name="phone" defaultValue={editingCustomer?.phone || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax_id">{t('reseller.customer.taxId')}</Label>
              <Input id="tax_id" name="tax_id" defaultValue={editingCustomer?.tax_id || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer_type">{t('reseller.customer.customerType')}</Label>
              <Select name="customer_type" defaultValue={editingCustomer?.customer_type || 'person'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">{t('reseller.customer.types.person')}</SelectItem>
                  <SelectItem value="company">{t('reseller.customer.types.company')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">{t('reseller.customer.status')}</Label>
              <Select name="status" defaultValue={editingCustomer?.status || 'active'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('reseller.customer.statuses.active')}</SelectItem>
                  <SelectItem value="inactive">{t('reseller.customer.statuses.inactive')}</SelectItem>
                  <SelectItem value="suspended">{t('reseller.customer.statuses.suspended')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">{t('reseller.customer.address')}</Label>
              <Input id="address" name="address" defaultValue={editingCustomer?.address || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">{t('reseller.customer.city')}</Label>
              <Input id="city" name="city" defaultValue={editingCustomer?.city || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="town">{t('reseller.customer.town')}</Label>
              <Input id="town" name="town" defaultValue={editingCustomer?.town || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">{t('reseller.customer.state')}</Label>
              <Input id="state" name="state" defaultValue={editingCustomer?.state || ''} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_code">{t('reseller.customer.postalCode')}</Label>
              <Input id="postal_code" name="postal_code" defaultValue={editingCustomer?.postal_code || ''} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">{t('reseller.customer.notes')}</Label>
            <Textarea id="notes" name="notes" defaultValue={editingCustomer?.notes || ''} />
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label>{t('reseller.customer.traccarLogin')}</Label>
            <Select
              value={traccarMode}
              onValueChange={(v) => {
                const mode = v as TraccarMode;
                setTraccarMode(mode);
                if (mode === 'new') {
                  setTimeout(() => {
                    const container = formRef.current?.parentElement;
                    if (container) container.scrollTop = container.scrollHeight;
                  }, 50);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {editingCustomer && (
                  <SelectItem value="none">{t('reseller.customer.unlinkUser')}</SelectItem>
                )}
                <SelectItem value="existing">{t('reseller.customer.selectUser')}</SelectItem>
                <SelectItem value="new">{t('reseller.customer.createNewUser')}</SelectItem>
              </SelectContent>
            </Select>

            {traccarMode === 'existing' && (
              <Popover open={traccarUserOpen} onOpenChange={setTraccarUserOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={traccarUserOpen}
                    className="w-full justify-between font-normal"
                  >
                    <span className={cn('truncate', !selectedTraccarUserLabel && 'text-muted-foreground')}>
                      {selectedTraccarUserLabel || t('reseller.customer.selectUser')}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('common.search')} className="h-9" />
                    <CommandList>
                      <CommandEmpty>{t('common.noResults')}</CommandEmpty>
                      <CommandGroup>
                        {[...traccarUsers]
                          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                          .map((user) => {
                            const value = `${user.name} ${user.email} ${user.id}`;
                            return (
                              <CommandItem
                                key={user.id}
                                value={value}
                                onSelect={() => {
                                  setSelectedTraccarUserId(String(user.id));
                                  setTraccarUserOpen(false);
                                }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', selectedTraccarUserId === String(user.id) ? 'opacity-100' : 'opacity-0')} />
                                <span className="truncate">{user.name} ({user.email})</span>
                              </CommandItem>
                            );
                          })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {traccarMode === 'existing' ? (
              <input type="hidden" name="email" value={selectedTraccarUser?.email ?? editingCustomer?.email ?? ''} />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('reseller.customer.emailUsername')}{traccarMode === 'new' ? ' *' : ''}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  required={traccarMode === 'new'}
                  defaultValue={editingCustomer?.email || ''}
                />
              </div>
            )}

            {traccarMode === 'new' && (
              <div className="flex gap-2">
                <Input
                  placeholder={t('reseller.customer.traccarPassword')}
                  type="text"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewUserPassword(generatePassword())}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  {t('reseller.customer.suggestPassword')}
                </Button>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || (traccarMode === 'existing' && !selectedTraccarUserId)}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingCustomer ? t('common.update') : t('common.create')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
